<template>
  <div class="row">
    <div class="col-md-12 titletext">
      Figuring out dosage when making edibles can be tricky, and many calculators vastly overestimate potency! Scientific Edibles takes the guesswork out of the infusion process by using real efficiency data. Answer the questions about your ingredients below to get an estimate of your dosage. Confused? Check out our <router-link to="/articles/how_to_use_our_calculator">guide</router-link> to using this calculator!
    </div>
    <div class="col-md-12 mt-3">
      <div class="card">
        <div class="card-body">
          <h4 class="card-title">Your Cannabis</h4>
          <h5>What is the potency of your cannabis (in %)? <info-icon text="The potency of the cannabis shows the percentage of THC/CBD in it."/></h5>
          <form v-on:submit.prevent>
            <small>Select from a preset</small>
            <div class="form-row">
              <div class="col">
                <b-form-select @change="onStrainChange" v-model="weedStrain" :options="strainOptions"></b-form-select>
              </div>
            </div>
            <small>or set it manually...</small>
            <div class="form-row">
              <div class="col" style="">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <div class="input-group-text badge-primary">THC</div>
                  </div>
                  <input type="number" min="0" class="form-control" v-model="thcPct" @change="onThcOrCbdChange">
                </div>
                <span style="z-index:999;color:dimgray;position:absolute;right:0.7rem;top:0.5rem"><strong>%</strong></span>
              </div>
              <div class="col">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <div class="input-group-text badge-success">CBD</div>
                  </div>
                  <input type="number" min="0" class="form-control" v-model="cbdPct" @change="onThcOrCbdChange">
                </div>
                <span style="z-index:999;color:dimgray;position:absolute;right:0.7rem;top:0.5rem"><strong>%</strong></span>
              </div>
            </div>
          </form>
          <h5 class="mt-4">What is the weight of your cannabis?</h5>
          <form v-on:submit.prevent>
            <div class="form-row">
              <div class="col">
                <div class="btn-toolbar" role="toolbar">
                  <div class="btn-group mr-2"><button type="button" @click="presetWeedWeight(0.125)" class="btn btn-outline-secondary mt-1"><img class="baggieicon" src="../assets/Icon-1.png"/> &frac18; oz</button></div>
                  <div class="btn-group mr-2"><button type="button" @click="presetWeedWeight(0.25)"  class="btn btn-outline-secondary mt-1"><img class="baggieicon" src="../assets/Icon-2.png"/> &frac14; oz</button></div>
                  <div class="btn-group mr-2"><button type="button" @click="presetWeedWeight(0.5)"   class="btn btn-outline-secondary mt-1"><img class="baggieicon" src="../assets/Icon-3.png"/> &frac12; oz</button></div>
                  <div class="btn-group mr-2"><button type="button" @click="presetWeedWeight(1.0)"   class="btn btn-outline-secondary mt-1"><img class="baggieicon" src="../assets/Icon-4.png"/>        1 oz</button></div>
                </div>
              </div>
            </div>
            <div class="form-row mt-2">
              <div class="col">
                <input type="number" min="0" class="form-control" v-model="weedAmount">
              </div>
              <div class="col">
                <select v-model="weedUnit" class="custom-select">
                  <option v-for="m in massMeasures" :key="m.abbr" :value="m.abbr">{{m.plural}} ({{m.abbr}})</option>
                </select>
              </div>
            </div>
          </form>
          <h5 class="mt-4">Did you decarb your cannabis? <info-icon text="Decarboxylation (decarbing) is the process of converting THCa/CBDa to THC/CBD. Doing this increases the potency of the final infusion. Certain products such as distilate and certain concentrates are already decarbed."/></h5>
          <form v-on:submit.prevent>
            <div class="form-row">
              <div class="col">
                <b-form-group>
                  <b-form-radio v-model="decarbMethod" name="decarb-radios" value="no">No <span v-if="decarbMethod === 'no'" style="color:red"><small>(You really should! See <a target="_blank" href="/articles/how_to_decarb_weed">here</a> how to.)</small></span></b-form-radio>
                  <b-form-radio v-model="decarbMethod" name="decarb-radios" value="alreadydecarb">No, my cannabis is already decarbed (distilate, RSO, etc).</b-form-radio>
                  <b-form-radio v-model="decarbMethod" name="decarb-radios" value="oven">Yes, I used the <a target="_blank" href="/articles/how_to_decarb_weed#oven_method">oven method</a>.</b-form-radio>
                  <b-form-radio v-model="decarbMethod" name="decarb-radios" value="crockpot">Yes, I used the <a target="_blank" href="/articles/how_to_decarb_weed#crockpot_method">mason jar/crockpot</a> method.</b-form-radio>
                </b-form-group>
              </div>
            </div>
          </form>
          <h5 class="mt-3">What type of cannabis are you using? <info-icon text="Choose 'plant' if you are using flower or some other plant based material, or 'concentrate' if you are using a concentrated form such as BHO or distilate. Having to infuse plant matter affects the infusion efficiency."/></h5>
          <form v-on:submit.prevent>
            <div class="form-row">
              <div class="col">
                <b-form-group>
                  <b-form-radio v-model="cannabisType" name="plant-radios" value="plant">Plant Based (flower, trim, etc)</b-form-radio>
                  <b-form-radio v-model="cannabisType" name="plant-radios" value="concentrate">Concentrate (distilate, BHO, etc)</b-form-radio>
                </b-form-group>
              </div>
            </div>
          </form>          
        </div>
      </div>
    </div>

    <div class="col-md-12">
      <div class="card mt-4">
        <div class="card-body">
          <h4 class="card-title">Your Solvent</h4>
          <h5>What solvent are you using? <info-icon text="Different solvents are more or less efficient at absorbing THC from plant matter."/></h5>
          <form v-on:submit.prevent>
            <div class="form-row">
              <div class="col">
                <select v-model="solvent" class="custom-select">
                  <option v-for="s in Object.keys(solvents)" :key="s" :value="s">{{solvents[s].name}}</option>
                </select>
              </div>
            </div>
          </form>
          <h5 class="mt-4">How much {{solvents[solvent].name.toLowerCase()}} are you infusing?</h5>
          <form v-on:submit.prevent>
            <div class="form-row mt-2 mb-2">
              <div class="col">
                <input type="number" min="0" class="form-control" v-model="solventAmount">
              </div>
              <div class="col">
                <select v-model="solventUnit" class="custom-select">
                  <option v-for="m in measures" :key="m.abbr" :value="m.abbr">{{m.plural}} ({{m.abbr}})</option>
                </select>
              </div>
            </div>
          </form>
          <small>Certain solvents eg. butter can reduce in weight during infusion due to moisture loss. If that happens, top up your infused solvent with regular solvent to get the same amount / potency.</small>

        </div>
      </div>
    </div>

    <div class="col-md-12">
      <div class="card mt-4">
        <div class="card-body">
          <h4 class="card-title">Your Recipe</h4>
          <h5>How much of your {{solvents[solvent].name.toLowerCase()}} do you need to use in your recipe? <info-icon text="If your recipe requires less solvent than you made, you can adjust it here."/></h5>
          <form v-on:submit.prevent>
            <div class="form-row mt-2">
              <div class="col-auto">
                <button type="button" @click="scaleRecipeSolventAmount(1)" class="btn btn-success">All of it!</button>
              </div>
              <div class="col">
                <input type="number" min="0" class="form-control" v-model="recipeSolventAmount">
              </div>
              <div class="col">
                <select v-model="recipeSolventUnit" class="custom-select">
                  <option v-for="m in measures" :key="m.abbr" :value="m.abbr">{{m.plural}} ({{m.abbr}})</option>
                </select>
              </div>
            </div>
          </form>
          <h5 class="mt-4">How many portions do you plan to make?</h5>
          <form v-on:submit.prevent>
            <div class="form-row mt-2">
              <div class="col">
                <input type="number" min="0" class="form-control" v-model="recipePortions">
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="col-md-12">
      <div class="card mt-4 mb-4">
        <div class="card-body">
          <h4 class="card-title">Results</h4>
          <div v-if="solventAmount > 0">
            Your {{solventAmount}}{{convertUnits().describe(solventUnit).abbr}} of {{solvents[solvent].name}} contains approximately <chem-indicator class="chemamount" typ='thc' :amount="totalThcMg"/> and <chem-indicator class="chemamount" typ='cbd' :amount="totalCbdMg"/>
            <table v-if="solventAmount > 0" class="table mt-3" style="width:100%;margin-left:auto; margin-right:auto;font-size:12pt">
              <thead class="thead-light">
                <tr>
                  <th>Unit</th>
                  <th>Dosage</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in displayUnits" :key="u">
                  <td>Per {{convertUnits().describe(u).singular}} ({{u}})</td>
                  <td><chem-indicator typ='thc' :amount="totalThcMg/convertDensity(solventUnit,u,solvent,solventAmount)"/> <chem-indicator typ='cbd' :amount="totalCbdMg/convertDensity(solventUnit,u,solvent,solventAmount)"/></td>
                </tr>
                <tr>
                  <td>
                    Per
                    <select v-model="customUnit" class="custom-select" style="display:inline-block;width:auto">
                      <option v-for="m in measures" :key="m.abbr" :value="m.abbr">{{m.singular}} ({{m.abbr}})</option>
                    </select>
                  </td>
                  <td><chem-indicator typ='thc' :amount="totalThcMg/convertDensity(solventUnit,customUnit,solvent,solventAmount)"/> <chem-indicator typ='cbd' :amount="totalCbdMg/convertDensity(solventUnit,customUnit,solvent,solventAmount)"/></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="recipePortions > 0 && recipeSolventAmount > 0 && solventAmount > 0">
            Each of your portions contains approximately <chem-indicator class="chemamount" typ='thc' :amount="thcMgPerPortion"/> and <chem-indicator class="chemamount" typ='cbd' :amount="cbdMgPerPortion"/><br/>
            Try consuming <strong>{{portionsFor10MgThc}}</strong> portions to get a <strong>~10mg THC dose</strong>.
              <div class="row"> 
                <div class="col-md-4" style="min-height:200px;max-height:200px;text-align:center;padding:10px">
                  <vue-speedometer
                    style="z-index:999"
                    :fluidWidth="false"
                    :height="200"
                    :minValue="0"
                    :maxValue="thcGaugeMaxValue"
                    :needleHeightRatio="0.5"
                    :value="Math.ceil(this.thcMgPerPortion)"
                    :currentValueText="dosageText"
                    :customSegmentStops="[0,5,10,20,50,thcGaugeMaxValue]"
                    :segmentColors="['limegreen','green','yellow','orange','red']"
                    :ringWidth="47"
                    :needleTransitionDuration="500"
                    :paddingHorizontal="0"
                    :paddingVertical="0"
                    :forceRender="true"
                    needleTransition="easeLinear"
                    needleColor="#444444"
                    textColor="#444444"
                  />
                </div>
                <div class="col-md-7 offset-md-1 mt-2">
                  <p><em>{{dosageParagraph}}</em></p>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col-md-12">
                  <hr/>
                  <p><strong style="color:red">WARNING: </strong>Even with accurate estimates, the potency of homemade edibles can vary somewhat. It is highly suggested to try a portion that corresponds to 10mg, wait 2 hours, and gauge the effects. Depending on how you feel, increase or decrease the portion size next time for the desired effect. Doing it this way reduces the chances of dosing too high on the first try.</p>
                </div>
              </div>
          </div>
          <div class="row mt-4">
            <div class="col-md-12">
              Use the link or the share buttons below to share this calculation.
              <div class="input-group mt-1">
                <input type="text" class="form-control urlinput" readonly ref="urlinput" v-model="customUrl">
                <div class="input-group-append">
                  <button class="btn btn-secondary" @click="copyCustomUrl" type="button"><strong>&#x2398; Copy</strong></button>
                </div>
              </div>
              <sharing class="mt-3" :url="customUrl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import strains from '@/strains.js'
import solvents from '@/solvents.js'
import measures from '@/measures.js'
import convert from 'convert-units'
import ChemIndicator from '@/components/ChemIndicator.vue'
import InfoIcon from '@/components/InfoIcon.vue'
import VueSpeedometer from "vue-speedometer"
import QueryString from 'query-string'
import Sharing from '@/components/Sharing.vue'

export default {
  name: 'Calculator',
  components: {
    ChemIndicator,
    VueSpeedometer,
    InfoIcon,
    Sharing,
  },
  data: function() {
    return {
      // General
      measures: measures,

      // Weed
      strains: strains,
      weedAmount: 0,
      weedUnit: 'g',
      weedStrain: null,
      thcPct: 0,
      cbdPct: 0,
      decarbMethod: 'no',
      cannabisType: 'plant',
      decarbEfficiencies: {
        no: 0.2,
        oven: 0.75,
        crockpot: 0.80,
        decarboxylator: 0.85,
        alreadydecarb: 1
      },

      // Solvent
      solvents: solvents,
      solvent: 'butter',
      solventAmount: 0,
      solventUnit: 'g',

      // Recipe
      recipeSolventAmount: 0,
      recipeSolventUnit: 'g',
      recipePortions: 0,

      // Results
      customUnit: 'cup',

      // URL
      urlParams: {
        weedAmount: parseFloat,
        weedUnit: x => x,
        weedStrain: x => x,
        thcPct: parseFloat,
        cbdPct: parseFloat,
        decarbMethod: x => x,
        cannabisType: x => x,
        solvent: x => x,
        solventAmount: parseFloat,
        solventUnit: x => x,
        recipeSolventAmount: parseFloat,
        recipeSolventUnit: x => x,
        recipePortions: parseFloat,
        customUnit: x => x
      }
    }
  },
  methods: {
    presetWeedWeight(w){
      this.weedAmount = w
      this.weedUnit = 'oz'
    },
    onStrainChange(){
      let s = this.strains[this.weedStrain]
      this.thcPct = s.thcPct
      this.cbdPct = s.cbdPct
      this.cannabisType = s.type
      if(!s.needsDecarb){
        this.decarbMethod = 'alreadydecarb'
      }
    },
    onThcOrCbdChange(){
      this.weedStrain = null
    },
    onSolventChange(){
      let s = this.solvents[this.solvent]
      this.solventEfficiency = s.efficiency
      this.isSolventLiquid = s.liquid
    },
    scaleRecipeSolventAmount(frac){
      this.recipeSolventAmount = this.solventAmount * frac
      this.recipeSolventUnit = this.solventUnit
    },
    to1dp(num){
      return Math.ceil(num*10) / 10
    },
    convertUnits(x){
      return convert(x)
    },
    convertDensity(from,to,solv,amount){
      let f = convert().describe(from).measure
      let t = convert().describe(to).measure
      let density = this.solvents[solv].density
      if(f == 'volume' && t == 'mass'){
        return convert(convert(amount).from(from).to(density.volume)*density.density).from(density.mass).to(to)
      }
      if(f == 'mass' && t == 'volume'){
        return convert(convert(amount).from(from).to(density.mass)/density.density).from(density.volume).to(to)
      }
      return convert(amount).from(from).to(to)
    },
    copyCustomUrl(){
      let el = this.$refs.urlinput
      el.select()
      el.setSelectionRange(0, 99999)
      document.execCommand("copy")
      this.$ga.event({
        eventCategory: 'calculator',
        eventAction: 'copy_custom_url',
        eventLabel: el.value
      })
    }
  },
  computed: {
    decarbEfficiency() {
      return this.decarbEfficiencies[this.decarbMethod]
    },
    massMeasures() {
      return this.measures.filter(x => x.measure === 'mass')
    },
    volumeMeasures() {
      return this.measures.filter(x => x.measure === 'volume')
    },
    totalThcMg() {
      let weedMg = convert(this.weedAmount*this.thcPct*0.01).from(this.weedUnit).to('mg')
      return weedMg * this.decarbEfficiency * (this.cannabisType == 'concentrate' ? 1 : this.solventEfficiency)
    },
    totalCbdMg() {
      let weedMg = convert(this.weedAmount*this.cbdPct*0.01).from(this.weedUnit).to('mg')
      return weedMg * this.decarbEfficiency * (this.cannabisType == 'concentrate' ? 1 : this.solventEfficiency)
    },
    thcMgPerPortion() {
      let frac = this.recipeSolventAmount/this.convertDensity(this.solventUnit,this.recipeSolventUnit,this.solvent,this.solventAmount)
      return frac * this.totalThcMg / this.recipePortions
    },
    cbdMgPerPortion() {
      let frac = this.recipeSolventAmount/this.convertDensity(this.solventUnit,this.recipeSolventUnit,this.solvent,this.solventAmount)
      return frac * this.totalCbdMg / this.recipePortions
    },
    portionsFor10MgThc() {
      return this.to1dp(10/this.thcMgPerPortion)
    },
    solventEfficiency() {
      return this.solvents[this.solvent].efficiency
    },
    isSolventLiquid() {
      return this.solvents[this.solvent].liquid
    },
    displayUnits() {
      if(this.isSolventLiquid){
        return ['ml','cup','Tbs']
      } else {
        return ['g','oz']
      }
    },
    dosageText() {
      let thc = this.thcMgPerPortion
      if(thc <= 5) {
        return 'Threshold Dose'
      }
      if(thc <= 20) {
        return 'Normal Dose'
      }
      if(thc <= 50) {
        return 'Strong Dose'
      }
      if(thc <= 500) {
        return 'Very Strong Dose'
      }
      if(thc > 500) {
        return 'Insanely Strong Dose'
      }
    },
    dosageParagraph() {
      let thc = this.thcMgPerPortion
      if(thc <= 5) {
        return 'Some mild effects. A good dose for beginners and those with a high sensitivity. Seasoned users may be unsatisfied.'
      }
      if(thc <= 20) {
        return 'A standard dose (10mg THC is the defacto standard dose). Recommended for confident first time / casual edible users. Sensitive users may occasionally be overwhelmed at this dose, though in general this should be manageable. This dosage is suggested as a good dosage to test your infusion.'
      }
      if(thc <= 50) {
        return 'A stronger than normal dose. Only experienced edible users should try this dose or higher. Can be manageable at lower levels, but inexperienced users can find themselves quickly overwhelmed, especially at the higher end of this dosage spectrum. Higher risk of nausea at this dose.'
      }
      if(thc <= 500) {
        return 'A very heavy dose. Only recommended for experienced users, or medical users with a high tolerance. Inexperienced users are likely to have a very unpleasant experience at this dose. High risk of nausea at this dose.'
      }
      if(thc > 500) {
        return 'An insanely strong dose. Experienced users only! For those that want to “go deep”...'
      }
    },
    thcGaugeMaxValue(){
      let ret = Math.max(100,100*Math.ceil(this.thcMgPerPortion/100))
      return ret
    },
    strainOptions() {
      let categories = {}
      for(let k of Object.keys(strains)) {
        let s = strains[k]
        if(categories[s.category] == undefined){
          categories[s.category] = [{value: k, text:s.name}]
        }else {
          categories[s.category].push({value: k, text:s.name})
        }
      }
      let options = []
      for(let k of Object.keys(categories)){
        options.push({
          label: k,
          options: categories[k]
        })
      }
      return options
    },
    customUrl() {
      let base = window.location.origin
      let vals = {}
      for(let p in this.urlParams){
        vals[p] = this[p]
      }
      return base + '?' + QueryString.stringify(vals)
    }
  },
  watch: {
    weedAmount(n,o) {
      if(n<0){
        this.weedAmount = 0
      }
    },
    solventAmount(n,o) {
      if(n<0){
        this.solventAmount = 0
      }
    },
    recipeSolventAmount(n,o) {
      if(n<0){
        this.recipeSolventAmount = 0
      }
    },
    recipePortions(n,o) {
      if(n<0){
        this.recipePortions = 0
      }
    },
    thcPct(n,o) {
      if(n<0){
        this.thcPct = 0
      }
      if(n>100){
        this.thcPct = 100
      }
    },
    cbdPct(n,o) {
      if(n<0){
        this.cbdPct = 0
      }
      if(n>100){
        this.cbdPct = 100
      }
    }
  },
  created() {
    for(let q of Object.keys(this.$route.query)){
      let val = this.$route.query[q]
      if(q in this.urlParams){
        this[q] = this.urlParams[q](val)
      }
    }
    this.$forceUpdate()
  }
}
</script>

<style scoped>
.fracbutton {
  width:25%;
}

.chemamount {
  font-size: 11pt;
}

.baggieicon {
  height: 2em;
  margin-right:0.2em;
}

.titletext {
  color: #444;
}

.urlinput {
  color:#111;
}
</style>
